/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import type { EuiComboBoxOptionOption } from '@elastic/eui'
import { EuiFormRow, EuiComboBox, EuiText, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useListDeploymentsQuery } from '@modules/cloud-lib/deployments/hooks'

type Props = {
  regionId: string
  value: string[]
  onChange: (value: string[]) => void
}

const DeploymentAssociationsField: React.FunctionComponent<Props> = ({
  regionId,
  value = [],
  onChange,
}) => {
  const { formatMessage } = useIntl()

  const { data, isLoading } = useListDeploymentsQuery()

  const options = useMemo(
    () =>
      (data?.deployments || [])
        .filter((deployment) => deployment.resources[0]?.region === regionId)
        .map((deployment) => ({
          label: deployment.name,
          value: deployment.id,
          key: deployment.id, // necessary for EuiComboBox to disambiguate resources with the same name
        })),
    [isLoading, regionId],
  )

  const selectedOptions = useMemo(
    () => options.filter((option) => value.some((id) => id === option.value)),
    [options, value],
  )

  const renderOption = (option: EuiComboBoxOptionOption<string>) => (
    <EuiFlexGroup direction='column' gutterSize='none'>
      <EuiFlexItem>
        <EuiText size='s'>
          <strong>{option.label}</strong>
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiText size='xs' color='subdued'>
          {option.value?.slice(0, 6)}...
        </EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  return (
    <EuiFormRow
      label={
        <FormattedMessage id='deploymentSelectField.label' defaultMessage='Select resources' />
      }
      helpText={
        <FormattedMessage
          id='deploymentSelectField.helpText'
          defaultMessage='This policy can be applied only to resources with the selected resource type.'
        />
      }
    >
      <EuiComboBox
        options={options}
        selectedOptions={selectedOptions}
        onChange={(newSelectedOptions) =>
          onChange(newSelectedOptions.map((option) => option.value || ''))
        }
        renderOption={renderOption}
        fullWidth={true}
        rowHeight={46}
        isClearable={true}
        isLoading={isLoading}
        data-test-id='deployment-select'
        placeholder={formatMessage({
          id: 'deploymentSelectField.placeholder',
          defaultMessage: 'Select',
        })}
        aria-label={formatMessage({
          id: 'deploymentSelectField.ariaLabel',
          defaultMessage: 'Select deployments',
        })}
      />
    </EuiFormRow>
  )
}

export default DeploymentAssociationsField
